import React from "react";
import { X, ShoppingCart, Wrench } from "lucide-react";
import useStore from "../../store/useStore";
import { getSizeStock } from "../../utils/inventoryUtils";

const Cart = ({ onCheckout }) => {
  const isOpen = useStore((state) => state.cartOpen);
  const cart = useStore((state) => {
    return state.cart;
  });

  const setCartOpen = useStore((state) => state.setCartOpen);
  const updateQuantity = useStore((state) => state.updateQuantity);
  const removeFromCart = useStore((state) => state.removeFromCart);

  const resizingTotal = cart.reduce(
    (sum, item) => sum + (item.customization?.resizingFee || 0) * item.quantity,
    0
  );
  const baseSubtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const cartTotal = baseSubtotal + resizingTotal;

  const handleCheckout = () => {
    setCartOpen(false);
    onCheckout();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setCartOpen(false)}
      />
      <div
        className="absolute top-0 right-0 flex flex-col w-full h-full max-w-md bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()} // ADDED: Prevent closing when clicking inside
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-[#e8e4f3] to-[#f5f3f9]">
          <div>
            <h3 className="font-serif text-2xl font-bold text-gray-900">
              Shopping Cart
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {cart.reduce((sum, item) => sum + item.quantity, 0)} items
            </p>
          </div>
          <button
            onClick={() => setCartOpen(false)}
            className="p-2 transition rounded-full hover:bg-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full">
                <ShoppingCart className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-lg text-gray-500">Your cart is empty</p>
              <p className="mt-2 text-sm text-gray-400">
                Add some beautiful jewelry to get started
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.map((item) => {
                // real per-size stock, straight off the catalog snapshot
                const stock = getSizeStock(item, item.selectedSize) || 1;
                return (
                <div
                  key={`${item.id}-${item.selectedSize || "no-size"}`} // UPDATED: Unique key with size
                  className="flex gap-4 pb-6 border-b border-gray-100"
                >
                  <img
                    src={item.tinyThumbs?.[0] || item.images?.[0]}
                    alt={item.name}
                    className="object-cover w-24 h-24 shadow-sm rounded-xl"
                  />
                  <div className="flex-1">
                    <h4 className="mb-1 font-semibold text-gray-900">
                      {item.name}
                    </h4>
                    {item.sku && (
                      <p className="font-mono text-xs text-gray-500">
                        SKU: {item.sku}
                      </p>
                    )}
                    <p className="text-xs text-[#d4af37] mb-2 font-medium">
                      {item.gender}
                    </p>
                    {/* NEW: Display selected size */}
                    {item.selectedSize && (
                      <p className="mb-1 text-xs text-gray-600">
                        Size: {item.selectedSize}
                      </p>
                    )}
                    {item.customization?.needsResizing && (
                      <p className="flex items-center gap-1 mb-1 text-xs text-blue-600">
                        <Wrench className="w-3 h-3" />
                        Resize: {item.selectedSize} →{" "}
                        {item.customization.customSize} (
                        {item.customization.resizingType}) (+$
                        {item.customization.resizingFee})
                      </p>
                    )}
                    <p className="text-lg font-bold text-gray-900">
                      $
                      {(
                        (item.price + (item.customization?.resizingFee || 0)) *
                        item.quantity
                      ).toLocaleString()}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3 px-2 py-1 bg-gray-100 rounded-full">
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.sku || item.id,
                              item.selectedSize,
                              -1
                            )
                          }
                          className="flex items-center justify-center font-semibold transition rounded-full w-7 h-7 hover:bg-white"
                        >
                          -
                        </button>
                        <span className="w-8 font-semibold text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.sku || item.id,
                              item.selectedSize,
                              1
                            )
                          }
                          disabled={item.quantity >= stock}
                          title={
                            item.quantity >= stock
                              ? `Only ${stock} in stock`
                              : undefined
                          }
                          className="flex items-center justify-center font-semibold transition rounded-full w-7 h-7 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() =>
                          removeFromCart(item.sku || item.id, item.selectedSize)
                        } // UPDATED: Pass size
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-gradient-to-r from-[#e8e4f3] to-[#f5f3f9]">
            <div className="mb-6 space-y-3">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-semibold">
                  ${baseSubtotal.toLocaleString()}
                </span>
              </div>
              {resizingTotal > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Resizing Fee</span>
                  <span className="font-semibold">
                    ${resizingTotal.toLocaleString()}
                  </span>
                </div>
              )}

              <div className="flex justify-between pt-3 text-xl font-bold text-gray-900 border-t border-gray-200">
                <span>Total</span>
                <span>${cartTotal.toLocaleString()}</span>
              </div>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white py-4 rounded-full font-semibold text-lg hover:from-[#2a2a5e] hover:to-[#1a1a3e] transition shadow-xl hover:shadow-2xl"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
